import React, { useState } from 'react';
import { ActivityIcon, BrainIcon, FolderIcon, GlobeIcon, PlugZapIcon, SparklesIcon, TableIcon, WorkflowIcon } from 'lucide-react';
import { PillTabs } from './PillTabs';
const ORANGE = '#EA580C';
const INK = '#2B211A';
const FADE = '#C9BDAE';
const BORDER = '#EAE4D9';
type NodeId = 'fs' | 'apps' | 'mem' | 'skills' | 'flows' | 'pulses' | 'tables' | 'cloudb' | 'agent' | 'mcp' | 'web' | 'slack' | 'teams' | 'gpt' | 'claude' | 'grok';
type GroupId = 'fp' | 'os' | 'pc';
type Node = {
  id: NodeId;
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
  sub?: string;
  group?: GroupId;
  /** Lucide icon for platform capabilities. */
  Icon?: React.ComponentType<{
    size?: number;
    strokeWidth?: number;
    color?: string;
  }>;
  /** Brand logo URL for third-party surfaces. */
  logo?: string;
};
const brand = (slug: string) => `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`;
const NODES: Node[] = [{
  id: 'fs',
  label: 'File System',
  x: 870,
  y: 606,
  w: 270,
  h: 44,
  Icon: FolderIcon
}, {
  id: 'apps',
  label: 'Connected Apps',
  x: 870,
  y: 60,
  w: 270,
  h: 84,
  sub: "Manage your team's connections to MCPs, APIs, and databases in one place",
  Icon: PlugZapIcon
}, {
  id: 'mem',
  label: 'Memory',
  x: 870,
  y: 172,
  w: 270,
  h: 84,
  sub: 'A company brain that answers in real time',
  Icon: BrainIcon
}, {
  id: 'skills',
  label: 'Skills',
  x: 870,
  y: 336,
  w: 270,
  h: 44,
  group: 'pc',
  Icon: SparklesIcon
}, {
  id: 'flows',
  label: 'Workflows',
  x: 870,
  y: 390,
  w: 270,
  h: 44,
  group: 'pc',
  Icon: WorkflowIcon
}, {
  id: 'pulses',
  label: 'Pulses',
  x: 870,
  y: 444,
  w: 270,
  h: 44,
  group: 'pc',
  Icon: ActivityIcon
}, {
  id: 'tables',
  label: 'Tables',
  x: 870,
  y: 498,
  w: 270,
  h: 44,
  group: 'pc',
  Icon: TableIcon
}, {
  id: 'cloudb',
  label: 'Cloud Browser',
  x: 870,
  y: 552,
  w: 270,
  h: 44,
  group: 'pc',
  Icon: GlobeIcon
}, {
  id: 'agent',
  label: 'Giga Runtime',
  x: 430,
  y: 348,
  w: 300,
  h: 82,
  sub: 'A fully featured harness with its own file system'
}, {
  id: 'mcp',
  label: 'Giga MCP Server',
  x: 430,
  y: 196,
  w: 300,
  h: 82,
  sub: "Giga's capabilities, available to any MCP-compatible tool"
}, {
  id: 'web',
  label: 'Giga Web Chat',
  x: 20,
  y: 442,
  w: 290,
  h: 56,
  group: 'fp'
}, {
  id: 'slack',
  label: 'Giga in Slack',
  x: 20,
  y: 520,
  w: 290,
  h: 56,
  group: 'fp',
  logo: brand('slack')
}, {
  id: 'teams',
  label: 'Giga in Teams',
  x: 20,
  y: 598,
  w: 290,
  h: 56,
  group: 'fp',
  logo: brand('microsoft-teams')
}, {
  id: 'gpt',
  label: 'ChatGPT Work',
  x: 20,
  y: 138,
  w: 290,
  h: 56,
  group: 'os',
  logo: brand('chatgpt')
}, {
  id: 'claude',
  label: 'Claude Cowork',
  x: 20,
  y: 216,
  w: 290,
  h: 56,
  group: 'os',
  logo: brand('claude-ai')
}, {
  id: 'grok',
  label: 'Grokbot',
  x: 20,
  y: 294,
  w: 290,
  h: 56,
  group: 'os',
  logo: brand('grok')
}];
const GROUPS: {
  id: GroupId;
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
}[] = [{
  id: 'fp',
  label: 'Giga surfaces',
  x: 4,
  y: 420,
  w: 322,
  h: 250
}, {
  id: 'os',
  label: 'Your existing services',
  x: 4,
  y: 116,
  w: 322,
  h: 250
}, {
  id: 'pc',
  label: 'Platform capabilities',
  x: 854,
  y: 316,
  w: 302,
  h: 354
}];
const EDGES: {
  a: string;
  b: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  straight?: boolean;
  arrow?: boolean;
}[] = [{
  a: 'fs',
  b: 'agent',
  x1: 730,
  y1: 400,
  x2: 870,
  y2: 628
}, {
  a: 'apps',
  b: 'mcp',
  x1: 730,
  y1: 224,
  x2: 870,
  y2: 102
}, {
  a: 'mem',
  b: 'mcp',
  x1: 730,
  y1: 240,
  x2: 870,
  y2: 214
}, {
  a: 'pc',
  b: 'mcp',
  x1: 730,
  y1: 252,
  x2: 854,
  y2: 470
}, {
  a: 'agent',
  b: 'mcp',
  x1: 580,
  y1: 348,
  x2: 580,
  y2: 278,
  straight: true,
  arrow: true
}, {
  a: 'model',
  b: 'agent',
  x1: 580,
  y1: 500,
  x2: 580,
  y2: 430,
  straight: true
}, {
  a: 'agent',
  b: 'fp',
  x1: 326,
  y1: 545,
  x2: 430,
  y2: 400
}, {
  a: 'mcp',
  b: 'os',
  x1: 326,
  y1: 241,
  x2: 430,
  y2: 232
}];
// Saved configurations. `off` is the exact set of switched-off nodes for that
// label; chainable labels combine when the off set is a disjoint union of them,
// so uncommon combinations still get sensible copy instead of a fallback.
const LABELS: {
  off: NodeId[];
  headline: string;
  tagline?: string;
  chain?: boolean;
}[] = [{
  off: [],
  headline: 'Use Giga alongside your existing tools.',
  tagline: 'The full Giga co worker plus the Giga MCP.'
}, {
  off: ['agent', 'fs', 'web', 'slack', 'teams'],
  headline: 'Use Giga alongside your existing tools, MCP only.',
  tagline: 'All the power of Giga for connected apps and memory, inside the tools you already have.',
  chain: true
}, {
  off: ['agent', 'fs', 'web', 'slack', 'teams', 'apps'],
  headline: 'Giga for memory over MCP.',
  tagline: "Use Giga's memory to improve how your organisation gets work done."
}, {
  off: ['agent', 'fs', 'web', 'slack', 'teams', 'mem'],
  headline: 'Giga as an MCP and connected tools gateway.',
  tagline: 'Let your team connect tools safely while you keep visibility.'
}, {
  off: ['gpt', 'claude', 'grok'],
  headline: 'Go all in on Giga.',
  tagline: 'Eliminate your Anthropic and OpenAI bills.',
  chain: true
}, {
  off: ['apps'],
  headline: 'Bring your own gateway.',
  tagline: 'Pair Giga with a tool like Composio or Executor to manage connections.',
  chain: true
}, {
  off: ['mem'],
  headline: 'Bring your own memory.',
  tagline: 'Pair Giga with an existing tool like Supermemory or Mem0.',
  chain: true
}];
const DEFAULT_HEADLINE = 'Use Giga alongside your existing tools.';
const DEFAULT_TAGLINE = 'All the power of Giga.';
const AGENT_DEPS: NodeId[] = ['fs', 'web', 'slack', 'teams'];
function resolveCopy(inactive: NodeId[]) {
  const key = (arr: string[]) => arr.slice().sort().join(',');
  const exact = LABELS.find(l => key(l.off) === key(inactive));
  if (exact) return {
    headline: exact.headline,
    tagline: exact.tagline || DEFAULT_TAGLINE
  };
  if (inactive.length) {
    let remaining = inactive.slice();
    const parts: typeof LABELS = [];
    const chainable = LABELS.filter(l => l.chain).sort((a, b) => b.off.length - a.off.length);
    for (const l of chainable) {
      if (l.off.every(id => remaining.includes(id))) {
        parts.push(l);
        remaining = remaining.filter(id => !l.off.includes(id));
      }
    }
    if (!remaining.length && parts.length) {
      const strip = (s: string) => s.replace(/\.$/, '');
      const headline = parts.map((p, i) => {
        const s = strip(p.headline);
        return i === 0 ? s : s.charAt(0).toLowerCase() + s.slice(1);
      }).join(' + ') + '.';
      const tagline = parts.map(p => p.tagline).filter(Boolean).join(' ') || DEFAULT_TAGLINE;
      return {
        headline,
        tagline
      };
    }
  }
  return {
    headline: DEFAULT_HEADLINE,
    tagline: DEFAULT_TAGLINE
  };
}
const MCP_ONLY_OFF: NodeId[] = ['agent', 'fs', 'web', 'slack', 'teams'];
const ALL_IN_OFF: NodeId[] = ['gpt', 'claude', 'grok'];
type WidgetState = {
  inactive: NodeId[];
  modelMode: 'giga' | 'byo';
};
// Exclusive presets: each names one whole configuration rather than a switch,
// so selecting one replaces the state instead of layering onto it. Individual
// blocks stay clickable, which can land the diagram in a state no preset
// describes — then nothing is selected, which is honest.
const PRESETS: {
  id: string;
  label: string;
  state: WidgetState;
}[] = [{
  id: 'alongside',
  label: 'Alongside your tools',
  state: {
    inactive: [],
    modelMode: 'giga'
  }
}, {
  id: 'mcp',
  label: 'MCP only',
  state: {
    inactive: MCP_ONLY_OFF,
    modelMode: 'giga'
  }
}, {
  id: 'allin',
  label: 'All in on Giga',
  state: {
    inactive: ALL_IN_OFF,
    modelMode: 'giga'
  }
}, {
  id: 'byo',
  label: 'BYO model',
  state: {
    inactive: [],
    modelMode: 'byo'
  }
}];
const sameSet = (a: NodeId[], b: NodeId[]) => a.length === b.length && a.slice().sort().join(',') === b.slice().sort().join(',');
export function ArchitectureWidget() {
  const [inactive, setInactive] = useState<NodeId[]>([]);
  const [modelMode, setModelMode] = useState<'giga' | 'byo'>('giga');
  const isOn = (id: string) => !inactive.includes(id as NodeId);
  const groupOn = (gid: GroupId) => NODES.some(n => n.group === gid && isOn(n.id));
  const toggle = (raw: NodeId) => {
    // The MCP server is always present, and the file system belongs to the
    // runtime — neither is independently switchable.
    if (raw === 'mcp' || raw === 'fs') return;
    let id = raw;
    const node0 = NODES.find(n => n.id === raw);
    if (node0?.group === 'fp') id = 'agent'; // Giga surfaces mirror the runtime
    if (AGENT_DEPS.includes(id) && !isOn('agent')) return;
    const node = NODES.find(n => n.id === id);
    let ids: NodeId[] = node?.group ? NODES.filter(n => n.group === node.group).map(n => n.id) : [id];
    if (id === 'agent') ids = ['agent', ...AGENT_DEPS];
    setInactive(s => {
      const turningOn = s.includes(id);
      const rest = s.filter(x => !ids.includes(x));
      return turningOn ? rest : [...rest, ...ids];
    });
  };
  const toggleGroup = (gid: GroupId) => {
    const first = NODES.find(n => n.group === gid);
    if (first) toggle(first.id);
  };
  const {
    tagline
  } = resolveCopy(inactive);
  const activePreset = PRESETS.find(p => p.state.modelMode === modelMode && sameSet(p.state.inactive, inactive))?.id ?? null;
  const endOn = (id: string) => GROUPS.some(g => g.id === id) ? groupOn(id as GroupId) : isOn(id);
  const modelOn = isOn('agent');
  const modelBtn = (active: boolean): React.CSSProperties => ({
    visibility: modelOn ? 'visible' : 'hidden',
    fontSize: 11.5,
    fontWeight: 600,
    cursor: 'pointer',
    padding: '4px 11px',
    borderRadius: 9999,
    border: 'none',
    background: active ? ORANGE : 'transparent',
    color: active ? '#FFFFFF' : 'rgba(255,255,255,0.55)',
    transition: 'background-color .2s cubic-bezier(0.23,1,0.32,1), color .2s cubic-bezier(0.23,1,0.32,1)'
  });
  return <div data-id="element-215" data-mp="8yzwu82acxm9">
      <style data-id="element-216" data-mp="4v1ulohnz6oz">{`
        @keyframes giga-dash-flow { from { stroke-dashoffset: 14 } to { stroke-dashoffset: 0 } }
        .giga-arch-edge { animation: giga-dash-flow 0.9s linear infinite }
        @media (prefers-reduced-motion: reduce) { .giga-arch-edge { animation: none } }
      `}</style>
      <div className="mx-auto max-w-2xl text-center" data-id="element-217" data-mp="skmi8koris4l">
        <p className="mb-4 text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-218" data-mp="slxk2eyndpt2">
          Architecture
        </p>
        <h2 className="font-title text-4xl font-bold leading-[1.05] tracking-tight text-[#2B211A] md:text-[56px]" data-id="element-219" data-mp="nrtbxsdnbbqe">
          Built to be modular.
        </h2>
        <p className="mx-auto mt-5 max-w-[52ch] text-lg leading-relaxed text-[#6B5D52]" data-id="element-220" data-mp="1gba1g244p46g">
          Use as much of Giga as you need alongside your existing tools.
        </p>
      </div>

      {/* Shorthand presets. Individual blocks stay clickable, but nothing on a
       diagram advertises that — these name the three configurations most
       people are actually deciding between. */}
      <div className="mx-auto mt-9 max-w-[60ch] text-center" data-id="element-221" data-mp="1ssktif65f06g">
        <PillTabs ariaLabel="Configuration" items={PRESETS.map(p => ({
        id: p.id,
        label: p.label
      }))} value={activePreset} onChange={id => {
        const preset = PRESETS.find(p => p.id === id);
        if (!preset) return;
        setInactive(preset.state.inactive);
        setModelMode(preset.state.modelMode);
      }} data-id="element-222" data-mp="1wufvllvwlhla" />
        <p className="mt-3 text-[13.5px] text-[#8A7A6A]" data-id="element-223" data-mp="2bkdgnsyjdr69">{tagline}</p>
      </div>

      <div className="mt-6" data-id="element-224" data-mp="2giom7rhstfio">
        <div className="overflow-x-auto" data-id="element-225" data-mp="2ghvp7i8n64dq">
          <div className="relative mx-auto h-[740px] w-[1160px]" data-id="element-226" data-mp="2kk0iugz3b10d">
            <svg width="1160" height="740" className="pointer-events-none absolute inset-0" data-id="element-227" data-mp="2kltmroibj74o">
              <defs data-id="element-228" data-mp="2pjd1sqmubnxo">
                <marker id="giga-arrow-on" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="4.5" markerHeight="4.5" orient="auto-start-reverse" data-id="element-229" data-mp="2pgqums3v3afz">
                  <path d="M0 0 L10 5 L0 10 z" fill="#B4A38E" data-id="element-230" data-mp="lga3w9mgq6lu" />
                </marker>
                <marker id="giga-arrow-off" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="4.5" markerHeight="4.5" orient="auto-start-reverse" data-id="element-231" data-mp="z6x2m1s83whx">
                  <path d="M0 0 L10 5 L0 10 z" fill="#ECE5DA" data-id="element-232" data-mp="udu6blw2a8y1" />
                </marker>
              </defs>
              {EDGES.map((e, i) => {
              const on = endOn(e.a) && endOn(e.b);
              const d = e.straight ? `M${e.x1} ${e.y1} L${e.x2} ${e.y2}` : `M${e.x1} ${e.y1} C${(e.x1 + e.x2) / 2} ${e.y1},${(e.x1 + e.x2) / 2} ${e.y2},${e.x2} ${e.y2}`;
              return <path key={i} d={d} fill="none" stroke={on ? '#B4A38E' : '#ECE5DA'} strokeWidth={3} strokeDasharray="7 7" markerEnd={e.arrow ? on ? 'url(#giga-arrow-on)' : 'url(#giga-arrow-off)' : undefined} className="giga-arch-edge" data-id="element-233" data-mp="1d7pxinmfstlk" data-mpi={i} />;
            })}
            </svg>

            {GROUPS.map(g => {
            const on = groupOn(g.id);
            return <React.Fragment key={g.id} data-id="element-234" data-mp="1zeu1y3wfb8qv">
                  <button type="button" aria-label={`Toggle ${g.label}`} onClick={() => toggleGroup(g.id)} className="absolute box-border rounded-[20px] transition-colors duration-300" style={{
                left: g.x,
                top: g.y,
                width: g.w,
                height: g.h,
                background: on ? '#FAF8F4' : '#F7F3EC',
                border: `1px solid ${on ? BORDER : '#EFE9DF'}`
              }} data-id="element-235" data-mp="1zdshj8tip9wh" />
                  <button type="button" tabIndex={-1} aria-hidden="true" onClick={() => toggleGroup(g.id)} className="absolute font-mono text-[11.5px] font-semibold uppercase tracking-[0.22em] transition-colors duration-300" style={{
                left: g.x + 6,
                top: g.y - 26,
                color: on ? ORANGE : '#D0C5B6'
              }} data-id="element-236" data-mp="1glzlf5j5j8kj">
                    {g.label}
                  </button>
                </React.Fragment>;
          })}

            {/* AI model — its own node because the choice of model is a
             separate decision from the runtime that calls it. */}
            <div className="absolute box-border flex items-center gap-3 rounded-2xl px-[22px] transition-colors duration-300" style={{
            left: 430,
            top: 500,
            width: 300,
            height: 82,
            background: modelOn ? INK : '#F4F0E9',
            border: `1px solid ${modelOn ? INK : BORDER}`,
            boxShadow: modelOn ? '0 8px 24px rgba(43,33,26,0.18)' : 'none'
          }} data-id="element-237" data-mp="27cm10spjndjr">
              <span className="size-[9px] shrink-0 rounded-full transition-colors duration-300" style={{
              background: modelOn ? ORANGE : '#D6CCC0',
              boxShadow: modelOn ? '0 0 0 3px rgba(234,88,12,0.35)' : 'none'
            }} data-id="element-238" data-mp="1jochjxfsz1vs" />
              <div className="flex min-w-0 flex-1 flex-col gap-1" data-id="element-239" data-mp="19pwty4fziar1">
                <div className="flex items-center justify-between gap-2" data-id="element-240" data-mp="15q5vdek7uqby">
                  <span className="font-title text-[21px] font-bold tracking-[-0.01em] transition-colors duration-300" style={{
                  color: modelOn ? '#FFFFFF' : FADE
                }} data-id="element-241" data-mp="3hczkwtwc6xtu">
                    AI model
                  </span>
                  <div className="flex gap-0.5 rounded-full bg-white/10 p-0.5" data-id="element-242" data-mp="3qdxsfm5zhnh6">
                    <button type="button" onClick={() => setModelMode('giga')} style={modelBtn(modelMode === 'giga')} data-id="element-243" data-mp="3vbpg23zlinfk">
                      Giga
                    </button>
                    <button type="button" onClick={() => setModelMode('byo')} style={modelBtn(modelMode === 'byo')} data-id="element-244" data-mp="32msdd97vaz7x">
                      BYO
                    </button>
                  </div>
                </div>
                <span className="text-[11.5px] font-medium leading-[1.3] transition-colors duration-300" style={{
                color: modelOn ? 'rgba(255,255,255,0.65)' : FADE
              }} data-id="element-245" data-mp="3ce51bpqjp58o">
                  {modelMode === 'giga' ? "Use Giga's default model" : 'Bring your own model'}
                </span>
              </div>
            </div>

            {NODES.map(n => {
            const on = isOn(n.id);
            const core = n.id === 'agent' || n.id === 'mcp';
            const locked = n.id === 'mcp' || n.id === 'fs';
            const Icon = n.Icon;
            return <button key={n.id} type="button" onClick={() => toggle(n.id)} disabled={locked} aria-pressed={on} className={`absolute box-border flex select-none items-center gap-3 rounded-2xl px-[22px] text-left transition-colors duration-300 ${locked ? 'cursor-default' : 'cursor-pointer'}`} style={{
              left: n.x,
              top: n.y,
              width: n.w,
              height: n.h,
              background: on ? core ? INK : '#FFFFFF' : '#F4F0E9',
              border: `1px solid ${on ? core ? INK : BORDER : BORDER}`,
              color: on ? core ? '#FFFFFF' : INK : FADE,
              boxShadow: on ? core ? '0 8px 24px rgba(43,33,26,0.18)' : '0 1px 3px rgba(43,33,26,0.07)' : 'none'
            }} data-id="element-246" data-mp="2gmtantbvf14w">
                  {Icon ? <span className="shrink-0 transition-opacity duration-300" style={{
                opacity: on ? 1 : 0.4
              }} data-id="element-247" data-mp="v2obcikcj351">
                      <Icon size={20} strokeWidth={1.9} color={on ? ORANGE : '#BFB3A4'} data-id="element-248" data-mp="m3uhgzyegqv6" />
                    </span> : n.logo ? <img src={n.logo} alt="" aria-hidden="true" className="size-[22px] shrink-0 object-contain transition-[filter] duration-300" style={{
                filter: on ? 'none' : 'grayscale(1) opacity(0.4)'
              }} data-id="element-249" data-mp="r1w2vrjat29t" /> : <span className="size-[9px] shrink-0 rounded-full transition-colors duration-300" style={{
                background: on ? ORANGE : '#D6CCC0',
                boxShadow: on ? `0 0 0 3px rgba(234,88,12,${core ? 0.35 : 0.16})` : 'none'
              }} data-id="element-250" data-mp="1isa7sy9c349c" />}
                  <span className="flex min-w-0 flex-col gap-0.5" data-id="element-251" data-mp="1xqaxvbnj6lvk">
                    <span className={core ? 'font-title text-[21px] font-bold tracking-[-0.01em]' : 'text-[16.5px] font-semibold'} data-id="element-252" data-mp="1xozwfihk5q7m">
                      {n.label}
                    </span>
                    {n.sub && <span className="text-[11.5px] font-medium leading-[1.3] transition-colors duration-300" style={{
                  color: on ? core ? 'rgba(255,255,255,0.65)' : '#8A7A6A' : FADE
                }} data-id="element-253" data-mp="2h4mwbo56l07">
                        {n.sub}
                      </span>}
                  </span>
                </button>;
          })}
          </div>
        </div>
      </div>

      <p className="mt-4 text-center text-[13px] text-[#8A7A6A]" data-id="element-254" data-mp="lde59b62txq0">
        Click any block or group to switch it off. The MCP server stays — it is
        what makes the rest reachable.
      </p>
    </div>;
}